import { useCallback } from 'react'
import { useHotelDate, useTenant } from 'src/app/hooks/context/context.hook'
import useUpdateReservedResourceHook from 'src/app/hooks/update-reserved-resource/update-reserved-resource.hook'
import { UpdateReservedResourceOutput } from 'src/app/hooks/update-reserved-resource/update-reserved-resource.types'
import { ReservationF } from '../types/reservation-detail.types'
import { ResourceOccupancyInput } from 'src/api/api.generated'
import { compareDates } from 'src/utils/date'

export default function useUpdateReservationResource() {
  const tenantId = useTenant().id
  const hotelDate = useHotelDate()
  const { updateReservedResourceMutation } = useUpdateReservedResourceHook()

  const updateResource = useCallback(
    async (params: { reservation: ReservationF; reservedResourceId: string; start: Date; end: Date; resourceId: string | null; resourceTypeId: string }): Promise<UpdateReservedResourceOutput> => {
      const { reservation, reservedResourceId, start, end, resourceId, resourceTypeId } = params
      const qReservedResource = reservation.reservedResources?.find((rr) => rr.id === reservedResourceId)
      if (!qReservedResource) {
        return { result: 'ERROR' }
      }

      const { resourceOccupancies, ...reservedResource } = qReservedResource

      let newResourceOccupancies: ResourceOccupancyInput[] = resourceOccupancies?.map((ro) => {
        return {
          end: ro.end,
          start: ro.start,
          resourceTypeId: ro.reservedResourceType.id,
          assignedResourceTypeId: ro.assignedResourceType.id,
          resourceId: ro.resource?.id,
          reservedResourceLayoutId: ro.reservedResourceLayoutId,
        }
      })
      if (qReservedResource.phase === 'RESERVATION' || compareDates(hotelDate, start) === 0) {
        newResourceOccupancies = newResourceOccupancies.map((resourceOccupancy) => {
          if (resourceOccupancy.start.valueOf() === start.valueOf() && resourceOccupancy.end.valueOf() === end.valueOf()) {
            return {
              ...resourceOccupancy,
              resourceId: resourceId,
            }
          } else {
            return {
              ...resourceOccupancy,
            }
          }
        })
      } else {
        let newEnd: Date | null = null
        newResourceOccupancies = newResourceOccupancies.map((resourceOccupancy) => {
          if (compareDates(hotelDate, resourceOccupancy.start) >= 0 && compareDates(hotelDate, resourceOccupancy.end) <= 0) {
            newEnd = resourceOccupancy.end
            return {
              ...resourceOccupancy,
              end: hotelDate,
            }
          } else {
            return {
              ...resourceOccupancy,
            }
          }
        })

        if (!newEnd) {
          return { result: 'ERROR' }
        }

        newResourceOccupancies.push({
          end: newEnd,
          start: hotelDate,
          resourceTypeId: resourceTypeId,
          resourceId: resourceId,
        })
      }

      // const newRR = { ...reservedResource, resourceOccupancies: newResourceOccupancies }

      const result = await updateReservedResourceMutation({
        reservedResourceId: reservedResource.id,
        input: {
          tenantId: tenantId,
          adults: reservedResource.adults,
          chargeCategoryId: reservedResource.chargeCategory.id,
          children: reservedResource.children,
          rateId: reservedResource.rate.id,
          reservationId: reservedResource.reservationId,
          resourceOccupancies: newResourceOccupancies,
        },
      })

      return result
    },
    [tenantId],
  )

  return updateResource
}
